import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { ICreateMaterialBrand } from "./interfaces/create-material-brand.interface";
import { IUpdateMaterialBrand } from "./interfaces/update-material-brand.interface";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import MaterialBrandAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-brand/material-brand.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";

const apiUrl = env.API_MATERIAL_URL;
const moduleName = "material_brand";

@Injectable({
  providedIn: "root",
})
export class MaterialBrandService extends AbstractService<
  MaterialBrandAttributes,
  ICreateMaterialBrand,
  IUpdateMaterialBrand
> {
  
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<MaterialBrandAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<MaterialBrandAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      const headers: Record<string, any> = {};
      // see order-customer-export for more info about this header
      // ctx: stands for context
      if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
      return this.http.get<IPaginationResult<MaterialBrandAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene marcas de materiales con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'code', 'name', 'externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param companyIds Company filter
   * @param status Status filter
   * @returns Paginated data
   *
   * Search fields: code, name, externalKeys.code
   * Array filters: companyIds, status
   */
  findAllWithCount(
    options: IPagination<MaterialBrandAttributes>,
    companyIds: number[],
    status?
  ): Observable<IPaginationResult<MaterialBrandAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtros de compañía, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<MaterialBrandAttributes>[] => {
      const deletedFilter: FilterFn<MaterialBrandAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        companyIds && companyIds.length > 0
          ? andWhere('company.id' as keyof MaterialBrandAttributes, Ops.in(...companyIds))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<MaterialBrandAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<MaterialBrandAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalCodeQueryObject: FilterFn<MaterialBrandAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("externalKeys.code" as keyof MaterialBrandAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<MaterialBrandAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchByExternalCodeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialBrandAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<MaterialBrandAttributes[]>> {
    const query = buildQuery<MaterialBrandAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<MaterialBrandAttributes[]>>(API_URL);
  }
}
